import React, { FC, ReactNode, isValidElement } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Cancel, Minus, Star } from '../icon';
import { Html } from '../html/Html';
import { Stack } from '../stack/Stack';
import { TickCircle } from '../icon/icons/TickCircle';
import { Typography } from '../typography/Typography';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import tokens from './tokens';

interface IconListProps {
  centerAlign?: boolean;
  contentSmall?: boolean;
  displayAsGrid?: boolean;
  icon?: ReactNode;
  iconColor?: ThemeColorsKeys;
  items?: IconListItemsProps[];
  large?: boolean;
  product?: boolean;
  verticalItems?: boolean;
  textAlignCenter?: boolean;
}

export interface IconListItemsProps {
  content?: string;
  icon?: ReactNode;
  title?: string;
}

interface StyledIconListProps {
  centerAlign?: boolean;
  displayAsGrid?: boolean;
  large?: boolean;
  verticalItems?: boolean;
  textAlignCenter?: boolean;
}

export const StyledIconList = styled.ul<StyledIconListProps>`
  ${({ theme: { media, space }, displayAsGrid }) => css`
    display: grid;
    column-gap: ${space.medium};
    row-gap: ${space.small};

    ${displayAsGrid &&
    css`
      row-gap: ${space.medium};

      @media (min-width: ${media.medium}) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (min-width: ${media.large}) {
        grid-template-columns: repeat(3, 1fr);
      }
    `}
  `};
`;

const StyledIconListItem = styled.li<StyledIconListProps>`
  ${({ theme: { space }, centerAlign, verticalItems, textAlignCenter }) => css`
    display: flex;
    align-items: ${centerAlign ? 'center' : 'start'};
    column-gap: ${space.small};
    row-gap: ${space.xxSmall};

    ${verticalItems &&
    css`
      flex-direction: column;
      align-items: center;
      text-wrap: pretty;
    `}

    ${textAlignCenter &&
    css`
      text-align: center;
    `}
  `};
`;

const StyledStack = styled(Stack)<StyledIconListProps>`
  ${({ theme: { space }, centerAlign }) => css`
    padding-top: ${centerAlign ? '0' : space.xxxSmall};
  `};
`;

const getIcon = (icon?: ReactNode, iconColor?: ThemeColorsKeys) => {
  if (isValidElement(icon)) {
    return icon;
  }

  if (typeof icon === 'string') {
    return {
      tick: <TickCircle color={iconColor ?? tokens.tickColour} />,
      cross: <Cancel color={iconColor ?? 'brandOne100'} />,
      minus: <Minus color={iconColor ?? 'brandOne100'} />,
      star: <Star color={iconColor ?? 'brandOne100'} />,
    }[icon];
  }

  return <TickCircle color={iconColor ?? tokens.tickColour} />;
};

const determineVariant = (large?: boolean, product?: boolean) => {
  if (large) return 'headingSmall';
  if (product) return 'bodyMedium';
  return 'bodyMediumBold';
};

export const IconList: FC<IconListProps> = ({
  centerAlign,
  contentSmall,
  displayAsGrid,
  icon,
  iconColor = tokens.tickColour,
  items,
  large,
  product,
  verticalItems,
  textAlignCenter,
  ...rest
}) => {
  if (!items) {
    return null;
  }

  return (
    <StyledIconList
      centerAlign={centerAlign}
      displayAsGrid={displayAsGrid}
      large={large}
      {...rest}
    >
      {items.map((item, index) => (
        <StyledIconListItem
          centerAlign={centerAlign}
          displayAsGrid={displayAsGrid}
          verticalItems={verticalItems}
          textAlignCenter={textAlignCenter}
          key={index}
        >
          {getIcon(item.icon ?? icon, iconColor)}

          <StyledStack gap="xxSmall" centerAlign={centerAlign}>
            {item.title && (
              <Typography
                component="h4"
                variant={determineVariant(large, product)}
              >
                {item.title}
              </Typography>
            )}
            {item.content && <Html small={contentSmall}>{item.content}</Html>}
          </StyledStack>
        </StyledIconListItem>
      ))}
    </StyledIconList>
  );
};

export default IconList;
